import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import typography from 'styles/typography';
import { useHistory } from 'react-router';
import _partition from 'lodash/partition';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';

import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import Errors from './Errors';
import Warnings from './Warnings';
import SummaryLine from './SummaryLine';

import * as MappingTypes from 'types/Mapping';
import { MAPPINGS_TAB, CSV_WIZARD_MAPPING_URL } from 'pages/Directory/consts';

interface Props {
  className?: string;
  csvFileName?: string;
  validationResponse: MappingTypes.ValidateMappingsResponse;
  onSubmit: () => void;
  dirty?: boolean;
  isCsvSource?: boolean;
  onUploadNewCsv: () => void;
}

function MappingsSummary({
  className,
  csvFileName,
  validationResponse,
  onSubmit,
  dirty,
  onUploadNewCsv,
  isCsvSource,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const { sampleDataCount, validationResults } = validationResponse;
  const sampleLimitExceeded = sampleDataCount >= 5000;
  const [errors, warnings] = _partition(validationResults, vr => vr.type === 'error');
  const numberOfIssues = validationResults.length || 'None';
  const onMappingsTab = history.location.pathname.includes(MAPPINGS_TAB);
  const isOnCsvUploadWizard = location.pathname.endsWith(CSV_WIZARD_MAPPING_URL);
  const submitButtonText = isOnCsvUploadWizard ? 'Initialize Your Sync' : 'Save Mappings';

  return (
    <Card className={classNames(classes.root, className)}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h5">Mappings Summary</Typography>

        <Typography color="textSecondary">
          Use this area to view any mapping issues that need to be addressed and to continue when
          finished making updates.
        </Typography>

        {csvFileName && (
          <>
            <SummaryLine
              children={
                <>
                  <Typography variant="h6">File Name:</Typography>&nbsp;
                  <Typography>{csvFileName}</Typography>
                </>
              }
            />

            <SummaryLine
              children={
                <>
                  <Typography variant="h6">Number of Rows:</Typography>&nbsp;
                  <Typography>
                    {sampleDataCount}
                    {sampleLimitExceeded && '+*'}
                  </Typography>
                </>
              }
            />
          </>
        )}

        {sampleLimitExceeded && (
          <SummaryLine
            children={
              <Typography color="textSecondary">
                *We’re only able to analyze up to 5000 rows of your uploaded CSV, so some warnings
                may be missed.
              </Typography>
            }
          />
        )}

        <Typography variant="h5" className={classes.issueCount}>
          Issues Found ({numberOfIssues}):
        </Typography>

        <SummaryLine
          children={
            <div className={classes.issueLine}>
              <Typography className={classes.issueTitle}>Errors -&nbsp;</Typography>
              <Typography>must be fixed before continuing:</Typography>
            </div>
          }
        />

        <Errors items={errors} />

        <SummaryLine
          children={
            <div className={classes.issueLine}>
              <Typography className={classes.issueTitle}>Warnings -&nbsp;</Typography>
              <Typography>recommended updates:</Typography>
            </div>
          }
        />

        <Warnings items={warnings} />

        {!onMappingsTab && isCsvSource && (
          <Typography className={classes.footer}>
            Need to correct something on your CSV? Click on “Upload New CSV” below.
          </Typography>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        {!onMappingsTab && isCsvSource && (
          <Button onClick={onUploadNewCsv} color="secondary">
            + upload new csv
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          disabled={!_isEmpty(errors) || (!dirty && onMappingsTab)}
          onClick={onSubmit}
          className={classes.saveButton}
        >
          {submitButtonText}
        </Button>
      </CardActions>
    </Card>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
    position: 'sticky',
    top: 0,
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  issueCount: {
    fontWeight: typography.fontWeight.semiBold,
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  issueTitle: {
    fontWeight: typography.fontWeight.semiBold,
  },
  issueLine: {
    display: 'flex',
    marginBottom: theme.spacing(),
  },
  footer: {
    marginTop: theme.spacing(1.25),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(),
  },
  saveButton: {
    marginLeft: 'auto',
    backgroundColor: theme.palette.secondary.main,
  },
}));

export default MappingsSummary;
